import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { defaultGroupBy } from '../../api/reportApi.js';
import { DatePresetOption, ReportFilters } from './components/ReportFilters.js';
import { ReportTable } from './components/ReportTable.js';
import { KeywordDetailPanel } from './components/KeywordDetailPanel.js';
import type {
  KeywordTrendWeeks,
  ReportEntry,
  ReportFilters as ReportFiltersType,
  ReportMetric,
} from '../../types/report.js';
import {
  addDays,
  formatDateInputValue,
  getDefaultRelevantWindow,
  getLatestAllowedDate,
  parseDateInput,
  toIsoDate,
} from '../../lib/date.js';
import { useReportQuery } from './hooks/useReportQuery.js';
import { useKeywordTrend } from './hooks/useKeywordTrend.js';
import {
  ColumnFilters,
  FilterableColumn,
  createEmptyColumnFilters,
  entryMatchesFilters,
} from './lib/columnFilters.js';

const validateRange = (fromValue: string, toValue: string, latestAllowedIso: string) => {
  const from = parseDateInput(fromValue);
  const to = parseDateInput(toValue);
  if (!from || !to) {
    return 'Zadejte platné datumy.';
  }

  if (from > to) {
    return 'Datum "od" nesmí být novější než "do".';
  }

  const latestAllowed = parseDateInput(latestAllowedIso);
  if (latestAllowed && to > latestAllowed) {
    return `Datum "do" nesmí být novější než ${latestAllowedIso}.`;
  }

  return undefined;
};

const getSortLabel = (field: ReportFiltersType['sortBy'], metric: ReportMetric) => {
  switch (field) {
    case 'campaignName':
      return 'Kampaň';
    case 'adGroupName':
      return 'Ad group';
    case 'usersCount':
      return 'Uživatelé';
    case 'totalValue':
      return metric === 'revenue' ? 'Celkové revenue' : 'Počet trialů';
    case 'averageValue':
      return metric === 'revenue' ? 'Průměr na uživatele' : 'Podíl trialů';
    case 'cost':
      return 'Náklady';
    case 'cpi':
      return 'CPI';
    case 'roi':
      return 'ROAS';
    default:
      return field;
  }
};

export const ReportDashboard = () => {
  const defaultWindow = useMemo(() => getDefaultRelevantWindow(), []);
  const latestAllowedExclusive = getLatestAllowedDate();
  const latestAllowedInclusiveDate = addDays(latestAllowedExclusive, -1);
  const latestAllowedDate = formatDateInputValue(latestAllowedInclusiveDate);

  const computePresetRange = useMemo(() => {
    const buildRange = (endInclusive: Date, length: number) => ({
      from: addDays(endInclusive, -(length - 1)),
      to: endInclusive,
    });

    return (preset: DatePresetOption): { from: Date; to: Date } => {
      const latestInclusive = latestAllowedInclusiveDate;
      switch (preset) {
        case 'last7':
          return buildRange(latestInclusive, 7);
        case 'prev7': {
          const latestBlock = buildRange(latestInclusive, 7);
          return buildRange(addDays(latestBlock.from, -1), 7);
        }
        case 'last30':
          return buildRange(latestInclusive, 30);
        case 'prev30': {
          const latestBlock = buildRange(latestInclusive, 30);
          return buildRange(addDays(latestBlock.from, -1), 30);
        }
        case 'custom':
        default:
          return {
            from: defaultWindow.from,
            to: addDays(defaultWindow.to, -1),
          };
      }
    };
  }, [defaultWindow.from, defaultWindow.to, latestAllowedInclusiveDate]);

  const [metric, setMetric] = useState<ReportMetric>('revenue');
  const [selectedPreset, setSelectedPreset] = useState<DatePresetOption>('last7');
  const initialRange = computePresetRange('last7');
  const [formRange, setFormRange] = useState(() => ({
    from: formatDateInputValue(initialRange.from),
    to: formatDateInputValue(initialRange.to),
  }));
  const [appliedRange, setAppliedRange] = useState(formRange);
  const [sort, setSort] = useState<{ by: ReportFiltersType['sortBy']; order: ReportFiltersType['sortOrder'] }>({
    by: 'totalValue',
    order: 'desc',
  });
  const [selectedTarget, setSelectedTarget] = useState<
    | {
        campaignName: string;
        campaignId: string | null;
        adGroupName: string | null;
        adGroupId: string | null;
        keywordName: string;
        keywordId: string | null;
        orgId: string | null;
      }
    | null
  >(null);
  const [detailWeeks, setDetailWeeks] = useState<KeywordTrendWeeks>(12);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(() =>
    createEmptyColumnFilters()
  );

  const validationError = validateRange(formRange.from, formRange.to, latestAllowedDate);

  const filters = useMemo<ReportFiltersType | undefined>(() => {
    const parsedFrom = parseDateInput(appliedRange.from);
    const parsedTo = parseDateInput(appliedRange.to);

    if (!parsedFrom || !parsedTo) {
      return undefined;
    }

    const toExclusive = addDays(parsedTo, 1);

    return {
      metric,
      from: toIsoDate(parsedFrom),
      to: toIsoDate(toExclusive),
      groupBy: defaultGroupBy,
      sortBy: sort.by,
      sortOrder: sort.order,
    };
  }, [appliedRange.from, appliedRange.to, metric, sort.by, sort.order]);

  const reportQuery = useReportQuery(filters);
  const keywordTrendQuery = useKeywordTrend(selectedTarget, detailWeeks);

  useEffect(() => {
    if (reportQuery.data) {
      const { by, order } = reportQuery.data.meta.sort;
      setSort((prev) => (prev.by === by && prev.order === order ? prev : { by, order }));
    }
  }, [reportQuery.data?.meta.sort.by, reportQuery.data?.meta.sort.order]);

  useEffect(() => {
    if (!selectedTarget || !reportQuery.data) {
      return;
    }

    const stillPresent = reportQuery.data.data.some((entry) => {
      if (entry.group.appleAdsType === 'BASIC') {
        return false;
      }
      const matchesTarget = selectedTarget.keywordId
        ? entry.identifiers.keywordId === selectedTarget.keywordId &&
          entry.identifiers.campaignId === selectedTarget.campaignId &&
          (entry.identifiers.adGroupId ?? null) === selectedTarget.adGroupId
        : entry.group.campaignName === selectedTarget.campaignName &&
          entry.group.keywordName === selectedTarget.keywordName &&
          (entry.group.adGroupName ?? null) === selectedTarget.adGroupName;

      if (!matchesTarget) {
        return false;
      }

      return entryMatchesFilters(entry, columnFilters);
    });

    if (!stillPresent) {
      setSelectedTarget(null);
      setIsDetailOpen(false);
    }
  }, [reportQuery.data, selectedTarget, columnFilters]);

  useEffect(() => {
    if (selectedTarget || !reportQuery.data) {
      return;
    }

    const firstSelectable = reportQuery.data.data.find(
      (entry) => entry.group.campaignName && entry.group.keywordName && entry.identifiers.keywordId
    );

    if (firstSelectable) {
      setSelectedTarget({
        campaignName: firstSelectable.group.campaignName!,
        campaignId: firstSelectable.identifiers.campaignId ?? null,
        adGroupName: firstSelectable.group.adGroupName ?? null,
        adGroupId: firstSelectable.identifiers.adGroupId ?? null,
        keywordName: firstSelectable.group.keywordName!,
        keywordId: firstSelectable.identifiers.keywordId ?? null,
        orgId: firstSelectable.identifiers.orgId ?? null,
      });
      setDetailWeeks(12);
      setIsDetailOpen(false);
    }
  }, [reportQuery.data, selectedTarget]);

  const handleCustomRangeChange = ({ field, value }: { field: 'from' | 'to'; value: string }) => {
    setFormRange((prev) => ({ ...prev, [field]: value }));
    if (selectedPreset !== 'custom') {
      setSelectedPreset('custom');
    }
  };

  const handleColumnFilterChange = (column: FilterableColumn, values: string[]) => {
    setColumnFilters((prev) => {
      const current = prev[column];
      const hasSameLength = current.length === values.length;
      const hasSameValues =
        hasSameLength && current.every((value, index) => value === values[index]);
      if (hasSameValues) {
        return prev;
      }
      return {
        ...prev,
        [column]: values,
      };
    });
  };

  const handleSortChange = (next: { by: ReportFiltersType['sortBy']; order: ReportFiltersType['sortOrder'] }) => {
    setSort(next);
  };

  const handleSelectRow = (entry: ReportEntry) => {
    if (!entry.group.campaignName || !entry.group.keywordName) {
      return;
    }

    setSelectedTarget({
      campaignName: entry.group.campaignName,
      campaignId: entry.identifiers.campaignId ?? null,
      adGroupName: entry.group.adGroupName ?? null,
      adGroupId: entry.identifiers.adGroupId ?? null,
      keywordName: entry.group.keywordName,
      keywordId: entry.identifiers.keywordId ?? null,
      orgId: entry.identifiers.orgId ?? null,
    });
    setDetailWeeks(12);
    setIsDetailOpen(true);
  };

  const handleWeeksChange = (weeks: KeywordTrendWeeks) => {
    setDetailWeeks(weeks);
  };

  const handlePresetChange = (preset: DatePresetOption) => {
    setSelectedPreset(preset);
    if (preset === 'custom') {
      return;
    }
    const range = computePresetRange(preset);
    const nextRange = {
      from: formatDateInputValue(range.from),
      to: formatDateInputValue(range.to),
    };
    setFormRange(nextRange);
    setAppliedRange((prev) =>
      prev.from === nextRange.from && prev.to === nextRange.to ? prev : nextRange
    );
  };

  useEffect(() => {
    if (selectedPreset !== 'custom') {
      return;
    }
    if (validationError) {
      return;
    }
    setAppliedRange((prev) =>
      prev.from === formRange.from && prev.to === formRange.to ? prev : formRange
    );
  }, [formRange, selectedPreset, validationError]);

  const closeDetail = () => setIsDetailOpen(false);

  return (
    <Stack spacing={3} sx={{ width: '100%', maxWidth: 1800, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 3, width: '100%' }}>
        <ReportFilters
          metric={metric}
          onMetricChange={setMetric}
          range={formRange}
          onCustomRangeChange={handleCustomRangeChange}
          preset={selectedPreset}
          onPresetChange={handlePresetChange}
          validationError={validationError}
          latestAllowedDate={latestAllowedDate}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, width: '100%' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} mb={2}>
          <div>
            <Typography variant="h5" component="h2">
              Výsledky
            </Typography>
            {reportQuery.data ? (
              (() => {
                const sortMeta = reportQuery.data.meta.sort;
                const sortLabel = getSortLabel(sortMeta.by, metric);
                const orderLabel = sortMeta.order === 'desc' ? 'sestupně' : 'vzestupně';
                return (
                  <Typography variant="body2" color="text.secondary">
                    Interval: {reportQuery.data.meta.window.from.slice(0, 10)} —{' '}
                    {reportQuery.data.meta.window.to.slice(0, 10)} · Aktualizováno{' '}
                    {new Date(reportQuery.data.meta.generatedAt).toLocaleString('cs-CZ')} · Řazeno:{' '}
                    {sortLabel} ({orderLabel})
                  </Typography>
                );
              })()
            ) : (
              <Typography variant="body2" color="text.secondary">
                Interval se načítá…
              </Typography>
            )}
          </div>
          {reportQuery.isFetching && !reportQuery.isLoading ? (
            <Typography color="primary">Načítám…</Typography>
          ) : null}
        </Stack>

        {reportQuery.isLoading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : null}

        {reportQuery.isError ? (
          <Alert severity="error" sx={{ my: 2 }}>
            Chyba při načítání dat: {reportQuery.error?.message ?? 'Nepodařilo se načíst report.'}
          </Alert>
        ) : null}

        {!reportQuery.isLoading && !reportQuery.isError && reportQuery.data ? (
          <ReportTable
            entries={reportQuery.data.data}
            metric={metric}
            sort={sort}
            onSortChange={handleSortChange}
            selectedTarget={selectedTarget}
            onSelectEntry={handleSelectRow}
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFilterChange}
          />
        ) : null}
      </Paper>

      <Dialog
        open={Boolean(isDetailOpen && selectedTarget)}
        onClose={closeDetail}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detail klíčového slova
          </Typography>
          <IconButton onClick={closeDetail} aria-label="Zavřít detail">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <KeywordDetailPanel
            target={selectedTarget}
            weeks={detailWeeks}
            onWeeksChange={handleWeeksChange}
            query={keywordTrendQuery}
          />
        </DialogContent>
      </Dialog>
    </Stack>
  );
};
